import React from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link, useParams } from "react-router-dom";
import { buildAuditDashboardCurrentInventoryStatusPath, type AuditDashboardInventoryMetric } from "../../utils/currentInventoryStatusPath";
import { auditDashboardTheme } from "../../theme";

interface AuditDashboardBackstockLinkProps {
  children: React.ReactNode;
  metric?: AuditDashboardInventoryMetric;
  reportDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  sx?: SxProps<Theme>;
}

export default function AuditDashboardBackstockLink({
  children,
  metric = "backstock_closing",
  reportDate,
  startDate,
  endDate,
  sx,
}: AuditDashboardBackstockLinkProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const hasRange = Boolean(startDate && endDate);
  const hasSingleDate = Boolean(reportDate);
  const isClickable = Boolean(storeId && (hasRange || hasSingleDate));
  const backstockPath = isClickable && storeId
    ? buildAuditDashboardCurrentInventoryStatusPath(storeId, {
        metric,
        reportDate: hasRange ? undefined : reportDate ?? undefined,
        startDate: hasRange ? startDate ?? undefined : undefined,
        endDate: hasRange ? endDate ?? undefined : undefined,
      })
    : null;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  const linkSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    border: "1px solid transparent",
    borderRadius: "8px",
    px: "6px",
    py: "3px",
    maxWidth: "100%",
    cursor: isClickable ? "pointer" : "default",
    userSelect: "text",
    transition: "border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease",
    "&:hover": isClickable
      ? {
          borderColor: "rgba(44, 108, 160, 0.45)",
          backgroundColor: "rgba(44, 108, 160, 0.06)",
        }
      : undefined,
    "&:focus-visible": isClickable
      ? {
          outline: `2px solid ${auditDashboardTheme.colors.blue}`,
          outlineOffset: "2px",
          borderRadius: "2px",
        }
      : undefined,
    ...sx,
  } as SxProps<Theme>;

  if (!backstockPath) {
    return (
      <Box component="span" sx={linkSx}>
        {children}
      </Box>
    );
  }

  return (
    <Box
      component={Link}
      to={backstockPath}
      onClick={handleClick}
      sx={linkSx}
    >
      {children}
    </Box>
  );
}
